/**
 * PersonalInboxSurface — the personal inbox: every mailbox the user connected
 * themselves, merged into one list.
 *
 * Backed by a single hook (usePersonalInboxEmails) that queries the
 * scope-qualified personal rows in db.communications for all sources (Gmail,
 * Outlook, CRM sequences), sorted server-side on a canonical comm_date, with
 * server-side filtering and skip/limit pagination.
 *
 * Infinite scroll accumulates rows across pages, de-duplicated on comm_id.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { useGmailStatus } from '../../hooks/useInbox';
import { useOutlookStatus } from '../../hooks/useOutlook';
import {
    useDeletePersonalMessages,
    usePersonalInboxEmails,
    usePersonalInboxSync,
    SYNC_BACKGROUND_MAX_PAGES,
} from '../../hooks/usePersonalInbox';
import PersonalInboxToolbar from './PersonalInboxToolbar';
import PersonalInboxTable from './PersonalInboxTable';
import { ConnectedFolderRail } from './FolderRail';
import TrashNoticeBar from './TrashNoticeBar';
import { buildThreadNav } from './threadNav';

import { draftIdsOf, draftPayload, partitionDrafts } from './draftRows';
import { leavesFolderAfter, mailStateToggles } from './mailRowState';
import { dispatchBulkStateAction } from './bulkDispatch';
import { useBulkTagPanel } from './BulkTagPanel';
import {
    EMPTY_PERSONAL_FILTERS,
    personalStateFromSearch,
    searchFromPersonalState,
} from './personalInboxUrl';
import useMailSelection from '../../hooks/useMailSelection';
import {
    useArchiveMail,
    useMoveMail,
    useReadMail,
    useSnoozeMail,
    useImportantMail,
    useEmptyTrash,
    useStarMail,
    resolveSnoozePreset,
} from '../../hooks/useMailState';
import { useDeleteDrafts } from '../../hooks/useMailDrafts';
import { resolveDisplayZone } from '../../lib/timezone';
import SyncInboxPrompt from './SyncInboxPrompt';
import { SyncBackfillPrompt } from './InitialSyncProgress';
import { InboxError, InboxSkeleton } from './inboxUtils';

const PAGE_SIZE = 40;

const EMPTY_FILTERS = EMPTY_PERSONAL_FILTERS;

// Mirrors BULK_DELETE_CAP in runtime/backend/routes/personal_inbox.py, which
// rejects a longer list with a 422 rather than truncating it.
const BULK_DELETE_CAP = 200;

export default function PersonalInboxSurface({ user }) {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    // --- Connection status ---
    const { data: gmailStatus } = useGmailStatus();
    const { data: outlookStatus } = useOutlookStatus();
    const gmailConnected = gmailStatus?.connected || false;
    const outlookConnected = outlookStatus?.connected || false;

    // --- Pagination state ---
    const [page, setPage] = useState(1);
    const [allRows, setAllRows] = useState([]);
    const [syncAttempted, setSyncAttempted] = useState(false);
    const [syncError, setSyncError] = useState(false);

    const syncAttemptedRef = useRef(false);
    const hasEverLoadedRowsRef = useRef(false);

    // --- Filter state (all filters are server-side) ---
    // --- Filter and folder state lives in the URL ---
    //
    // Not `useState`. Opening a message unmounts this surface, so component
    // state meant Back remounted it with every filter, the source pill and the
    // folder reset to default — the reported "apply a filter, switch platform,
    // open a mail, press Back and the filters are gone". It also cost a
    // refresh, a shared link and a bookmark. The unified lane has always kept
    // these in `location.search`; this is the same arrangement, in the
    // personal lane's own vocabulary (`personalInboxUrl.js`).
    const { filters, folder } = useMemo(
        () => personalStateFromSearch(location.search),
        [location.search]
    );

    // True while a bulk restore has hidden every row and its move is still in
    // flight. `surfaceIsEmpty` must not read that hidden-empty list as "this
    // inbox has no mail": the whole surface would swap for the sync prompt
    // (or the connect prompt) while the restore is pending, unmounting the
    // list mid-mutation — the instrumented failure that started this fix.
    const [bulkMoveInFlight, setBulkMoveInFlight] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const { data, isLoading, isFetching, error } = usePersonalInboxEmails(
        page,
        PAGE_SIZE,
        { ...filters, folder }
    );

    const syncMutation = usePersonalInboxSync();
    const isSyncing = syncMutation.isPending;
    const deleteMutation = useDeletePersonalMessages();

    /**
     * Reset the accumulator when the QUERY changes — never from a handler.
     *
     * Clicking the folder you are already in used to empty the list: the
     * handler called `setAllRows([])`, but `folder`, `page` and `filters` were
     * all unchanged, so React Query served the same cached object, the
     * accumulate effect below never re-ran, and `allRows` stayed `[]` while the
     * header went on reporting "0 of 398". Same shape for re-applying a filter
     * that was already set.
     *
     * Deriving the reset from the query key is what makes that impossible: the
     * thing that clears the rows and the thing that refills them now fire off
     * the same change. `page` is deliberately NOT in the key — "Load more"
     * bumps it, and wiping the accumulator there is the bug in reverse.
     *
     * **Declared ABOVE the accumulator on purpose.** React runs effects in
     * declaration order within a commit, and a folder change dirties both: put
     * this second and it clears the rows the accumulator just fetched, then
     * never re-runs to refill them — the reported bug, reintroduced one commit
     * later. The selection is cleared in its own effect further down, because
     * `clearSelection` does not exist yet at this point in the file.
     */
    const filterKey = [
        folder,
        filters.search, filters.sender, filters.dateFrom, filters.dateTo,
        filters.replied, filters.unread, filters.starred, filters.hasAttachment,
        filters.provider, filters.direction, filters.sortBy, filters.tag,
    ].join('\u0000');

    const filterKeyRef = useRef(filterKey);
    useEffect(() => {
        if (filterKeyRef.current === filterKey) return;
        filterKeyRef.current = filterKey;
        setPage(1);
        setAllRows([]);
    }, [filterKey]);

    // --- Accumulate rows across pages, de-duplicated on comm_id ---
    // comm_id is the primary key for every source; external_id is absent on
    // sequence emails, so it cannot be the dedup key.
    //
    // Guard: when filters change, placeholderData serves the OLD query's rows
    // while the new one is in flight. Skip that window so the user never sees
    // results that contradict the filter they just set.
    useEffect(() => {
        if (!data?.emails) return;
        if (isFetching && page === 1) return;
        if (page === 1) {
            setAllRows(data.emails);
        } else {
            setAllRows((prev) => {
                // Refresh in place, THEN append. The old version only appended
                // rows it had not seen, so a row already in the accumulator
                // kept whatever state it was fetched with: star it or mark it
                // read while scrolled past page 1 and the refetch changed
                // nothing on screen. It looked intermittent because it
                // depended on how far the reader had scrolled.
                const incoming = new Map(data.emails.map((r) => [r.comm_id, r]));
                const refreshed = prev.map((r) => incoming.get(r.comm_id) || r);
                const held = new Set(prev.map((r) => r.comm_id));
                return [...refreshed, ...data.emails.filter((r) => !held.has(r.comm_id))];
            });
        }
    }, [data, page, isFetching]);

    useEffect(() => {
        if (data?.emails?.length) hasEverLoadedRowsRef.current = true;
    }, [data?.emails]);

    // --- Selection ---
    // Keyed on comm_id everywhere. The table previously derived a row id from
    // `email_id || communication_id || tracking_id || comm_id` while select-all
    // wrote comm_id, so any row carrying one of the earlier fields could never
    // appear checked after a select-all.
    //
    // No `filters`/`allowEscalation` any more: selection is exactly what is
    // ticked (#710), so the hook never needs to describe the view as a
    // predicate and the personal lane's unfaithful `sender` filter stops being
    // a special case.
    const selection = useMailSelection({
        entries: allRows,
        total: data?.total ?? 0,
    });

    const { clear: clearSelection, buildTarget, selectedIds } = selection;
    // #646 — the bulk bar's Tag action: one request over the selected conversations.
    const bulkTag = useBulkTagPanel({ inboxId: null, selection, entries: allRows });

    // The selection belongs to the rows that were on screen. Same key, its own
    // effect, because `clearSelection` only exists this far down the file.
    useEffect(() => { clearSelection(); }, [filterKey, clearSelection]);

    // Writes replace rather than push: a filter tweak is not a place in
    // history, and pushing one meant Back stepped through every keystroke of a
    // search before it left the inbox.
    const writeUrl = useCallback(
        (nextFilters, nextFolder) => {
            navigate(
                {
                    pathname: location.pathname,
                    search: searchFromPersonalState(nextFilters, nextFolder),
                },
                { replace: true }
            );
        },
        [navigate, location.pathname]
    );

    const handleFilterChange = useCallback(
        (newFilters) => writeUrl(newFilters, folder),
        [writeUrl, folder]
    );

    const handleSelectFolder = useCallback(
        (nextFolder) => writeUrl(filters, nextFolder),
        [writeUrl, filters]
    );


    // #646 — a tag is a second filter over the folder, kept in the URL beside it.
    // `null` (clicking the active tag again) clears it.
    const handleSelectTag = useCallback(
        (tagId) => writeUrl({ ...filters, tag: tagId || undefined }, folder),
        [writeUrl, filters, folder]
    );

    // --- Infinite scroll ---
    const hasMore = data?.has_more ?? false;
    const isLoadingMore = isFetching && page > 1;

    const loadMore = useCallback(() => {
        if (hasMore && !isFetching) {
            setPage((p) => p + 1);
        }
    }, [hasMore, isFetching]);

    // --- Sync ---
    // syncMutation.mutate (not mutateAsync): the mutationFn uses
    // Promise.allSettled and never rejects, so there is nothing to catch.
    // However, individual providers can fail — the onSuccess callback
    // inspects `failedLabels` to surface errors and set syncError so the
    // empty-after-sync state can show a retry instead of a blank table.
    const handleSync = useCallback(({ quiet = false, maxPages } = {}) => {
        setSyncAttempted(true);
        setSyncError(false);
        syncMutation.mutate(
            // `maxPages` undefined means "use the hook's default" — the full
            // walk. Only the quiet background pass narrows it.
            {
                gmailConnected,
                outlookConnected,
                ...(maxPages ? { maxPages } : {}),
                ...(quiet ? { quiet: true } : {}),
            },
            {
                onSuccess: ({ synced, repaired, failedLabels }) => {
                    if (failedLabels?.length) {
                        // Always recorded — the empty-after-sync state reads it
                        // to offer a retry — but only announced when a person
                        // asked for this sync.
                        setSyncError(true);
                        if (!quiet) toast.error(`${failedLabels.join(' and ')} sync failed`);
                        return;
                    }
                    if (quiet) return;
                    if (synced || repaired) {
                        const parts = [];
                        if (synced) parts.push(`${synced} new`);
                        if (repaired) parts.push(`${repaired} updated`);
                        toast.success(`Synced — ${parts.join(', ')}`);
                    }
                    // "Already up to date" is NOT announced. It is the
                    // overwhelmingly common outcome, and a toast for "nothing
                    // happened" trains people to dismiss the toasts that matter.
                },
            }
        );
    }, [syncMutation, gmailConnected, outlookConnected]);

    const handleRefresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['personal-inbox', 'emails'] });
    }, [queryClient]);

    // --- Mail state ---
    //
    // Declared HERE rather than beside `handleAction`, because Delete is now a
    // move (see below) and `runTrash` closes over `moveMail`. A `useCallback`
    // whose dependency array names a `const` declared further down throws
    // "Cannot access 'moveMail' before initialization" on the first render —
    // the whole surface, not just the button.
    const starMail = useStarMail();
    const importantMail = useImportantMail();
    const readMail = useReadMail();
    const archiveMail = useArchiveMail();
    const snoozeMail = useSnoozeMail();
    const moveMail = useMoveMail();
    const emptyTrash = useEmptyTrash();
    // Drafts are not in `communications`, so neither `moveMail` nor the bulk
    // delete endpoint can touch one — see `draftRows.js`.
    const deleteDrafts = useDeleteDrafts(null);
    const [confirmEmptyTrash, setConfirmEmptyTrash] = useState(false);

    const runEmptyTrash = useCallback(() => {
        setConfirmEmptyTrash(false);
        emptyTrash.mutate({}, {
            onSuccess: (res) => {
                const deleted = res?.data?.deleted ?? 0;
                setAllRows([]);
                toast.success(
                    deleted === 1 ? '1 message deleted' : `${deleted} messages deleted`
                );
            },
            onError: (err) =>
                toast.error(err?.response?.data?.detail || "Couldn't empty Trash"),
        });
    }, [emptyTrash]);

    // --- Delete ---
    //
    // Delete is TWO verbs, and conflating them is the reported defect. Outside
    // Trash it is a MOVE — reversible, exactly what Gmail, Outlook and every
    // other mail client do — and the row stays recoverable from the Trash
    // view. Only inside Trash does it tombstone, which is the one-way door.
    //
    // Before this, the list's Delete called `bulk-delete` from every view, and
    // that endpoint sets `deleted_at`. `deleted_at: None` is in the base filter
    // of every list query, so the row left the mailbox entirely: not in Trash,
    // not restorable, gone from the UI for good. The READER already did the
    // right thing (`moveMail` → `folder: 'trash'`), so the two surfaces
    // disagreed about what the same word meant.
    const runTrash = useCallback(
        (ids, count) => {
            if (!ids.length) return;
            const removing = new Set(ids);
            setAllRows((prev) => prev.filter((r) => !removing.has(r.comm_id)));
            clearSelection();
            moveMail.mutate(
                { target: { ids }, folder: 'trash' },
                {
                    onSuccess: (res) => {
                        // The endpoint answers `{matched, modified}` and this
                        // read neither. `onSuccess` fires on any 2xx, so a move
                        // that matched NOTHING was reported exactly like one
                        // that moved forty — and because the rows are dropped
                        // optimistically above, the list agreed with the toast
                        // until the next refetch put them back.
                        //
                        // That is precisely how deleting a draft passed for
                        // working: a draft id matches no `communications` row,
                        // so `set_folder` returned `matched: 0` with a 200.
                        // Drafts now leave through `runDraftDelete`, but the
                        // check stays — it is what makes the NEXT bug of this
                        // shape visible instead of silent. The unified lane's
                        // `settle` helper has read `modified` since #693.
                        if (res?.data?.modified === 0) {
                            toast.warning(
                                'Nothing moved — it may have been deleted already'
                            );
                            handleRefresh();
                            return;
                        }
                        toast.success(
                            count === 1 ? 'Moved to Trash' : `${count} conversations moved to Trash`
                        );
                    },
                    onError: (err) => {
                        toast.error(err?.response?.data?.detail || 'Could not move to Trash');
                        handleRefresh();
                    },
                }
            );
        },
        [moveMail, clearSelection, handleRefresh]
    );

    const runPurge = useCallback(
        async (ids, count) => {
            if (!ids.length) return;
            // Drop the rows locally first so the list doesn't hold deleted mail
            // on screen for a refetch round-trip.
            const removing = new Set(ids);
            setAllRows((prev) => prev.filter((r) => !removing.has(r.comm_id)));
            clearSelection();
            try {
                // Chunked to the endpoint's BULK_DELETE_CAP. Since #521 a
                // selected row fans out to every message in its conversation,
                // so selecting a screenful of threads reaches the cap far
                // sooner than selecting a screenful of messages did — and the
                // failure is a 422 that reports no partial success, after the
                // rows have already been dropped from the list optimistically.
                for (let i = 0; i < ids.length; i += BULK_DELETE_CAP) {
                    await deleteMutation.mutateAsync(ids.slice(i, i + BULK_DELETE_CAP));
                }
                // Reported in conversations: `result.deleted` counts the rows
                // tombstoned, which for one collapsed row is the whole thread —
                // "5 messages deleted" after deleting a single conversation is
                // accurate and still reads as a mistake.
                toast.success(
                    count === 1 ? 'Conversation deleted' : `${count} conversations deleted`
                );
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || 'Failed to delete. Refreshing…'
                );
                handleRefresh();
            }
        },
        [deleteMutation, handleRefresh, clearSelection]
    );

    /**
     * Discard drafts. Always permanent — there is no draft Trash to move to.
     *
     * Optimistic, like the two above it, and rolled back the same way: a draft
     * the server refused is a draft that is still there, and the list must not
     * be the last place that knows it.
     */
    const runDraftDelete = useCallback(
        (draftIds, count) => {
            if (!draftIds.length) return;
            const removing = new Set(draftIds);
            const snapshot = allRows.filter((r) => removing.has(r.comm_id));
            setAllRows((prev) => prev.filter((r) => !removing.has(r.comm_id)));
            clearSelection();
            deleteDrafts.mutate(draftIds, {
                onSuccess: ({ deleted, failed }) => {
                    if (failed) {
                        // Partial success is reported as partial. The rows that
                        // genuinely failed come back with the refetch.
                        toast.error(
                            `${failed} of ${count} draft${count === 1 ? '' : 's'} could not be deleted`
                        );
                        handleRefresh();
                        return;
                    }
                    toast.success(
                        deleted === 1 ? 'Draft deleted' : `${deleted} drafts deleted`
                    );
                },
                onError: (err) => {
                    setAllRows((prev) => {
                        const have = new Set(prev.map((r) => r.comm_id));
                        const back = snapshot.filter((r) => !have.has(r.comm_id));
                        return back.length ? [...prev, ...back] : prev;
                    });
                    toast.error(
                        err?.response?.data?.detail || 'Could not delete the draft'
                    );
                    handleRefresh();
                },
            });
        },
        [allRows, deleteDrafts, clearSelection, handleRefresh]
    );

    // #521: a row is a CONVERSATION, so deleting it deletes the conversation.
    // `thread_comm_ids` is every message the backend collapsed behind the row;
    // sending only `comm_id` would tombstone the root and leave the row on
    // screen re-rendered around the next message, which reads as a delete that
    // failed. The fallback keeps this working against a backend that predates
    // the field, and against the cached rows an older page may still hold.
    const threadIds = useCallback(
        (item) => (item?.thread_comm_ids?.length ? item.thread_comm_ids : [item.comm_id]),
        []
    );

    const handleBulkDelete = useCallback(() => {
        const selected = allRows.filter((r) => selectedIds.has(r.comm_id));
        // Drafts leave through a different door — they are not in
        // `communications`, so `threadIds` would hand the move endpoint an id
        // it silently matches nothing with (see `draftRows.js`).
        const { drafts, messages } = partitionDrafts(selected);
        // De-duplicated: two selected rows can only share an id if the same
        // conversation appeared twice, but the bulk endpoint is capped and a
        // repeat would spend that cap for nothing.
        const ids = Array.from(new Set(messages.flatMap(threadIds)));
        setPendingDelete({
            ids,
            draftIds: draftIdsOf(drafts),
            conversations: selected.length,
            // TWO flags, because the two halves have different fates and one
            // flag could only lie about one of them.
            //
            // It was `folder === 'trash' || drafts.length > 0`, which drove the
            // copy — so a draft in the selection made the dialog say
            // "Permanently delete 3 conversations… Delete forever" while
            // `confirmDelete`'s `permanent && folder === 'trash'` guard fell
            // through to `runTrash` and merely MOVED the messages. The user was
            // told their mail was destroyed and it was recoverable.
            draftsPermanent: drafts.length > 0,
            permanent: folder === 'trash',
        });
    }, [allRows, selectedIds, threadIds, folder]);

    // Bulk Restore — the other door out of Trash, at selection scale.
    //
    // Fails CLOSED on missing entries: a selected id that is no longer in the
    // accumulator cannot be expanded to its conversation, so acting on the
    // root alone would tombstone nothing-or-worse server-side while the toast
    // claimed success — the exact failure shape #521 exists for. Loaded ids
    // only, for the same reason the resolver never offers these verbs under a
    // predicate.
    const handleBulkRestore = useCallback(() => {
        // Read the LIVE selection object, not a destructured copy. The
        // destructure happens at render; a selection change after that render
        // (a queued event, a checkbox tick React has not committed yet) would
        // be invisible to a captured Set — the handler would act on the ids
        // the checkboxes USED to show. `selection.selectedIds` is whatever the
        // table was last rendered with — the live contract.
        const live = selection.selectedIds;
        const selected = allRows.filter((r) => live.has(r.comm_id));
        if (!selected.length) return;
        // Fail closed: every selected id must still be a LOADED row. A
        // selection carrying an id the accumulator no longer holds cannot be
        // expanded to its conversation — acting on a stale id set would send
        // a partial target while the toast claimed success.
        if (selected.length !== live.size) {
            toast.error('Some selected messages are no longer loaded — refresh and try again');
            return;
        }
        const ids = selected.flatMap(threadIds);
        const count = selected.length;
        // Snapshot the rows about to leave: onError rolls them back locally
        // (handleRefresh's refetch is the backstop, not the only line of
        // defense — the snapshot makes the undo independent of the refetch
        // succeeding).
        const snapshot = selected;
        // Drop the rows optimistically, like `runTrash` does — but the table
        // must stay mounted while the move is in flight: emptying `allRows`
        // to `[]` in an unfiltered Inbox view trips `surfaceIsEmpty`, which
        // swaps the WHOLE surface for the sync prompt (the instrumented run
        // caught exactly that: the table unmounted mid-mutation and the
        // pending move was invisible). Hiding the rows instead of unmounting
        // the list around the mutation keeps the failure visible where it
        // happened; onError refetches, which restores anything the backend
        // refused.
        const leaving = new Set(ids);
        setAllRows((prev) => prev.filter((r) => !leaving.has(r.comm_id)));
        clearSelection();
        // The in-flight flag: true the moment the rows are hidden, false once
        // the backend has answered either way. It holds `surfaceIsEmpty` at
        // false for the mutation's round-trip so the empty list the restore
        // just created is not mistaken for an empty INBOX.
        setBulkMoveInFlight(true);
        moveMail.mutate(
            { target: { ids }, folder: null },
            {
                onSuccess: () => {
                    setBulkMoveInFlight(false);
                    toast.success(
                        count === 1 ? 'Conversation restored' : `${count} conversations restored`
                    );
                },
                onError: (err) => {
                    setBulkMoveInFlight(false);
                    // Roll the snapshot back BEFORE the refetch: the rows the
                    // optimistic hide removed reappear immediately, even if
                    // handleRefresh()'s refetch itself fails. Any rows that
                    // genuinely did move arrive via the refetch and are
                    // de-duplicated by comm_id in the accumulate effect.
                    setAllRows((prev) => {
                        const have = new Set(prev.map((r) => r.comm_id));
                        const restored = snapshot.filter((r) => !have.has(r.comm_id));
                        return restored.length ? [...prev, ...restored] : prev;
                    });
                    toast.error(err?.response?.data?.detail || 'Could not restore');
                    handleRefresh();
                },
            }
        );
    }, [allRows, selection, threadIds, clearSelection, moveMail, handleRefresh]);

    const handleRowDelete = useCallback(
        (item) => {
            if (item?.is_draft) {
                setPendingDelete({
                    ids: [],
                    draftIds: draftIdsOf([item]),
                    subject: item.subject,
                    conversations: 1,
                    draftsPermanent: true,
                    permanent: false,
                });
                return;
            }
            setPendingDelete({
                ids: threadIds(item),
                draftIds: [],
                subject: item.subject,
                conversations: 1,
                draftsPermanent: false,
                permanent: folder === 'trash',
            });
        },
        [threadIds, folder]
    );

    const confirmDelete = useCallback(() => {
        const ids = pendingDelete?.ids || [];
        const draftIds = pendingDelete?.draftIds || [];
        const count = pendingDelete?.conversations ?? ids.length + draftIds.length;
        const permanent = pendingDelete?.permanent;
        setPendingDelete(null);
        // Both halves run when the selection was mixed. Counts are reported per
        // store, because "3 deleted" spanning two endpoints is one number that
        // cannot be right if either half fails.
        if (draftIds.length) runDraftDelete(draftIds, draftIds.length);
        if (!ids.length) return;
        const messageCount = Math.max(count - draftIds.length, 1);
        // `permanent` is now the MESSAGE half's fate alone, so the branch and
        // the copy read the same flag and cannot disagree.
        if (permanent) runPurge(ids, messageCount);
        else runTrash(ids, messageCount);
        // `folder` is deliberately NOT a dependency: the fate of the message
        // half is decided when the dialog opens and carried on `permanent`, so
        // reading the folder again here could act on a view the user has since
        // navigated away from.
    }, [pendingDelete, runPurge, runTrash, runDraftDelete]);

    // --- Navigation ---
    // `threadNav` is the reader's "1 of 148" and its ‹ ›. Only this surface
    // knows which list the row was sitting in — see `threadNav.js`.
    const openThread = useCallback(
        (item, extra) => {
            navigate(`/inbox/email/${item.comm_id}`, {
                state: {
                    email: item,
                    threadNav: buildThreadNav(allRows, item.comm_id, data?.total),
                    ...extra,
                },
            });
        },
        [navigate, allRows, data?.total]
    );

    const handleRowClick = useCallback(
        (item) => {
            // A draft has no thread to read — it has never been sent. Clicking
            // one reopens the composer on it, which is also the ONLY way a
            // draft gets back into compose now that opening compose no longer
            // restores the last one.
            if (item?.is_draft) {
                navigate('/inbox/compose', { state: { draft: draftPayload(item) } });
                return;
            }
            openThread(item);
        },
        [openThread, navigate]
    );

    const handleReply = useCallback(
        (item) => openThread(item, { initialMode: 'reply' }),
        [openThread]
    );

    // One handler for both the row hover strip and the bulk bar, because they
    // mean the same verbs over different targets: a row action names one id, a
    // bulk action uses whatever the selection currently is (ids, or a
    // predicate after the user escalated). `buildTarget(null)` is the personal
    // lane — the unified surface passes its inbox id instead.
    const handleAction = useCallback(
        (key, item, { source = 'row' } = {}) => {
            // ── Bulk dispatch ────────────────────────────────────────────
            //
            // The bulk bar names a DIRECTION (`markRead`), not a toggle
            // (`markUnread` was both) — a bulk action has no row to read the
            // current state from, so `mailStateToggles(null)` cannot decide
            // for it (#710). The bulk bar sends `{ source: 'bulk' }` with
            // `item: null`; branching on that explicit argument, never on
            // `item === null`, keeps the row switch below the single source
            // of row semantics.
            if (source === 'bulk') {
                // The guard that used to sit here refused a destructive verb
                // under a predicate selection. There is no predicate selection
                // to refuse any more, and a guard against an unreachable state
                // is the clutter #710 asked to be rid of.
                const target = buildTarget(null);
                // The 8 state verbs are one shared executor (bulkDispatch.js):
                // identical payloads and toast handling on both surfaces. What
                // stays here is this lane's own destructive dispatch — the
                // confirm-dialog move/delete/restore over conversations.
                if (dispatchBulkStateAction(key, {
                    target,
                    mutations: { readMail, archiveMail, importantMail, snoozeMail },
                    user,
                    clearSelection,
                })) {
                    return;
                }
                switch (key) {
                    case 'delete':
                        handleBulkDelete();
                        return;
                    case 'restore':
                        handleBulkRestore();
                        return;
                    default:
                        if (key === 'tag') {
                            bulkTag.openPanel();
                            return;
                        }
                        toast.error(`Unsupported bulk action: ${key}`);
                }
                return;
            }

            // ── Row dispatch ─────────────────────────────────────────────
            //
            // A row action targets that row; `item` is the conversation.
            //
            // `threadIds(item)`, NOT `[item.comm_id]`. A row is a COLLAPSED
            // conversation, and `comm_id` is only its root. Archiving the root
            // left every other message in the thread untouched, so the list
            // re-grouped around the next one and the row came straight back —
            // while the API honestly reported one row modified and the toast
            // said "Archived". That is the reported "some emails cannot be
            // archived from the list but can be from the reader": the reader
            // has always sent the whole thread. Delete already did this; star,
            // archive, read and snooze did not.
            const target = item ? { ids: threadIds(item) } : buildTarget(null);
            const toggles = mailStateToggles(item);

            // Drop the row now when the verb moves it out of the view on
            // screen. A refetch alone cannot do this past page 1: the
            // accumulator replaces only page 1, so an archived row on page 3
            // simply stayed. Delete has always done this; the rest did not.
            const dropIfItLeaves = () => {
                if (!item || !leavesFolderAfter(folder, key, toggles)) return;
                const leaving = new Set(threadIds(item));
                setAllRows((prev) => prev.filter((r) => !leaving.has(r.comm_id)));
            };
            const settle = (verb) => ({
                onSuccess: (res) => {
                    if (!item) clearSelection();
                    dropIfItLeaves();
                    // The endpoint answers `{matched, modified}` and this used
                    // to read neither: `onSuccess` fires on any 2xx, so a
                    // mutation that changed NOTHING was reported exactly like
                    // one that changed forty. That is what let the root-only
                    // archive say "Archived" while the row stayed put, and it
                    // would hide the next bug of that shape just as well.
                    const modified = res?.data?.modified;
                    if (modified === 0) {
                        toast.warning('Nothing changed — the message may have moved already');
                        return;
                    }
                    toast.success(verb);
                },
                onError: (err) =>
                    toast.error(err?.response?.data?.detail || `Could not ${verb.toLowerCase()}`),
            });

            switch (key) {
                case 'reply':
                    if (item) handleReply(item);
                    return;
                case 'delete':
                    if (item) handleRowDelete(item);
                    else handleBulkDelete();
                    return;
                case 'important':
                    // The verb names the direction. Both of these said
                    // "Updated", which is true of every action on this switch
                    // and therefore tells the user nothing — and on a toggle
                    // it is the one word that cannot confirm what happened.
                    // The direction is already computed in `toggles`.
                    importantMail.mutate(
                        { target, important: toggles.important },
                        settle(toggles.important
                            ? 'Marked as important'
                            : 'Removed from important'),
                    );
                    return;
                case 'star':
                    starMail.mutate(
                        { target, starred: toggles.starred },
                        settle(toggles.starred ? 'Starred' : 'Unstarred'),
                    );
                    return;
                case 'markUnread':
                    // #626 — both directions. `read` was hardcoded false, so on
                    // an already-unread row the write hit `set_read`'s own
                    // transition guard, changed nothing, and still toasted
                    // success. The icon flips too (`actionDef` in MailRow), so
                    // the control says which way it is about to go.
                    readMail.mutate(
                        { target, read: toggles.read },
                        settle(toggles.read ? 'Marked read' : 'Marked unread'),
                    );
                    return;
                case 'archive':
                    archiveMail.mutate(
                        { target, archived: toggles.archived },
                        settle(toggles.archived ? 'Archived' : 'Moved to Inbox'),
                    );
                    return;
                case 'snooze': {
                    // #626 — unsnoozing is `until: null`, which the backend has
                    // always supported and nothing ever sent.
                    if (toggles.unsnooze) {
                        snoozeMail.mutate({ target, until: null }, settle('Unsnoozed'));
                        return;
                    }
                    // Resolved CLIENT-side in the viewer's display zone and
                    // sent as an absolute instant: the API accepts no symbolic
                    // preset, and resolving server-side would use the stored
                    // PROFILE zone rather than the one being read in.
                    const until = resolveSnoozePreset(
                        'tomorrow',
                        new Date(),
                        resolveDisplayZone({ user }),
                    );
                    snoozeMail.mutate(
                        { target, until: until ? until.toISOString() : null },
                        settle('Snoozed'),
                    );
                    return;
                }
                case 'restore':
                    // The other door out of Trash. `folder: null` is the
                    // move endpoint's own "back to the Inbox" — it has always
                    // accepted it, and nothing ever sent it, so this lane's
                    // Trash could be emptied and never un-done.
                    moveMail.mutate({ target, folder: null }, settle('Restored'));
                    return;
                case 'spam':
                    moveMail.mutate({ target, folder: 'spam' }, settle('Moved to spam'));
                    return;
                default:
                    // An action the surface contract lists but this handler has
                    // no case for is a wiring bug, not a user error.
                    toast.error(`Unsupported action: ${key}`);
            }
        },
        [
            buildTarget, clearSelection, user, threadIds, folder, bulkTag,
            handleReply, handleRowDelete, handleBulkDelete, handleBulkRestore,
            starMail, importantMail, readMail, archiveMail, snoozeMail, moveMail,
        ],
    );


    const anyConnected = gmailConnected || outlookConnected;

    // Both full-surface prompts REPLACE the toolbar, so they must only appear
    // when the inbox is genuinely empty — never when a filter merely matched
    // nothing. Without this guard, filtering to zero results swapped the whole
    // UI for "sync your inbox" and took the filter controls with it: the user
    // could see no messages and had no way to clear the filter that hid them.
    //
    // A non-default FOLDER narrows the view for exactly the same reason a
    // filter does, and was not counted: clicking Starred on an inbox with
    // nothing starred replaced the whole surface — rail included — with "sync
    // your inbox", leaving no way back to the folder you came from. An empty
    // Starred is not an empty inbox.
    const hasActiveFilters = Boolean(
        filters.search ||
        filters.sender ||
        filters.dateFrom ||
        filters.dateTo ||
        filters.provider ||
        filters.tag ||
        filters.replied !== undefined ||
        filters.unread !== undefined
    );

    /** Any view narrower than "the inbox, unfiltered". */
    const isNarrowedView = hasActiveFilters || folder !== 'inbox';

    const surfaceIsEmpty =
        allRows.length === 0 && !isLoading && !isSyncing && !isFetching && !isNarrowedView
        && !bulkMoveInFlight;

    const showSyncPrompt = surfaceIsEmpty && anyConnected && !syncAttempted;

    const showConnectPrompt = surfaceIsEmpty && !anyConnected;

    // F02 fix: When auto-sync fires and ALL providers fail, the user sees a
    // blank table with no explanation. surfaceIsEmpty is true (no rows),
    // syncAttempted is true (so showSyncPrompt is false), and there is no
    // error or retry path. This condition catches that state and shows a
    // retry prompt instead of the blank table.
    const showSyncError =
        syncError &&
        syncAttempted &&
        allRows.length === 0 &&
        !isLoading &&
        !isSyncing &&
        !isFetching &&
        !isNarrowedView;

    // Auto-trigger first sync on mount when conditions are met.
    // Guarded by syncAttemptedRef (survives StrictMode double-invoke).
    //
    // F08 fix: syncMutation is a new object on every render (React Query
    // returns a fresh mutation object), so listing it in deps causes the
    // effect to re-evaluate on every render even though the mutate function
    // is stable. We call handleSync via a ref so the effect only depends on
    // the conditions that actually matter, and the full onSuccess/onError
    // callback chain (including setSyncError) is reused.
    const handleSyncRef = useRef(null);
    handleSyncRef.current = handleSync;

    useEffect(() => {
        if (!anyConnected) return;
        if (isNarrowedView) return;
        if (hasEverLoadedRowsRef.current) return;
        if (syncAttemptedRef.current) return;
        if (syncMutation.isPending) return;
        syncAttemptedRef.current = true;
        setSyncAttempted(true);
        handleSyncRef.current();
    }, [anyConnected, isNarrowedView, syncMutation.isPending, gmailConnected, outlookConnected]);

    /**
     * The background pass — "sync it in the background" without the banner.
     *
     * Server-side delivery is authoritative for newly ingested mail. This
     * browser-driven pass remains a bounded recovery floor for provider/API
     * lag, missed live events, and mail already available from a connected
     * provider before durable delivery is reconciled.
     *
     * Quiet by construction: no banner, no toast, and the Sync button's spinner
     * is the only visible difference. Three guards keep it cheap — a hidden tab
     * does nothing, a sync already in flight is never doubled, and a filtered
     * view is skipped because the auto-sync path treats filters as "the user is
     * looking for something specific".
     *
     * `isSyncing` is read through a ref rather than listed as a dependency: it
     * flips on every pass, and a dependency on it would tear down and re-arm
     * the interval each time, so the clock would restart mid-cycle and the
     * cadence would drift.
     *
     * Sixty seconds is the fallback cadence and the pass walks ONE provider page
     * (`SYNC_BACKGROUND_MAX_PAGES`). That keeps recovery bounded per open tab
     * while the server path handles normal delivery. The list's own 60s refetch
     * remains the replay floor for an event missed by the browser.
     *
     * It stops when this surface unmounts and does no work in a hidden tab:
     * live server delivery and durable unread state continue independently of
     * this page, while this pass only helps an open, visible surface recover.
     */
    const BACKGROUND_SYNC_MS = 60 * 1000;
    const backgroundSyncGuardRef = useRef({ syncing: false, filtered: false });
    backgroundSyncGuardRef.current = { syncing: isSyncing, filtered: isNarrowedView };

    useEffect(() => {
        if (!anyConnected) return undefined;
        const timer = setInterval(() => {
            const { syncing, filtered } = backgroundSyncGuardRef.current;
            if (syncing || filtered) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            handleSyncRef.current?.({ quiet: true, maxPages: SYNC_BACKGROUND_MAX_PAGES });
        }, BACKGROUND_SYNC_MS);
        return () => clearInterval(timer);
    }, [anyConnected, BACKGROUND_SYNC_MS]);

    const showInitialSyncSkeleton =
        anyConnected &&
        !isNarrowedView &&
        !hasEverLoadedRowsRef.current &&
        (isSyncing || isLoading);

    // Counted in CONVERSATIONS, which is what the reader selected. `ids` is the
    // messages behind them, so counting that instead would offer to "delete 7
    // messages" after a click on a single row.
    const deleteCount = pendingDelete?.conversations ?? pendingDelete?.ids?.length ?? 0;
    const deletePermanent = Boolean(pendingDelete?.permanent);
    // The two halves are counted SEPARATELY. `conversations` is the selected
    // row count, which in a mixed selection includes drafts — so "1 draft +
    // 2 messages" read as "3 conversations" while `confirmDelete` was already
    // doing the `count - draftIds.length` correction internally. One number
    // for two different things is how the copy and the action drift apart.
    const deleteDraftCount = pendingDelete?.draftIds?.length ?? 0;
    const deleteMessageCount = Math.max(deleteCount - deleteDraftCount, 0);
    // A selection of drafts alone is described in drafts. Calling one a
    // "conversation" would be wrong twice over: it has no thread, and the
    // dialog would offer a Trash it is never going to.
    const deleteDraftsOnly = deleteDraftCount > 0 && !pendingDelete?.ids?.length;
    const deleteMixed = deleteDraftCount > 0 && deleteMessageCount > 0;
    const deleteLabel = useMemo(() => {
        const verb = deletePermanent ? 'Permanently delete' : 'Move';
        const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
        if (deleteDraftsOnly) {
            if (deleteCount === 1 && pendingDelete?.subject) {
                return `Delete draft "${pendingDelete.subject}"?`;
            }
            return deleteCount === 1
                ? 'Delete this draft?'
                : `Delete ${deleteCount} drafts?`;
        }
        if (deleteMixed) {
            // Both fates, named. The drafts go for good; the messages do
            // whatever this folder does.
            return deletePermanent
                ? `Delete ${plural(deleteDraftCount, 'draft')} and permanently `
                  + `delete ${plural(deleteMessageCount, 'conversation')}?`
                : `Delete ${plural(deleteDraftCount, 'draft')} and move `
                  + `${plural(deleteMessageCount, 'conversation')} to Trash?`;
        }
        if (deleteCount === 1 && pendingDelete?.subject) {
            return deletePermanent
                ? `Permanently delete "${pendingDelete.subject}"?`
                : `Move "${pendingDelete.subject}" to Trash?`;
        }
        if (deleteCount === 1) {
            return deletePermanent
                ? 'Permanently delete this conversation?'
                : 'Move this conversation to Trash?';
        }
        return deletePermanent
            ? `Permanently delete ${deleteCount} conversations?`
            : `${verb} ${deleteCount} conversations to Trash?`;
    }, [deleteCount, pendingDelete, deletePermanent, deleteDraftsOnly,
        deleteMixed, deleteDraftCount, deleteMessageCount]);

    if (error) {
        return <InboxError message="Failed to load emails." onRetry={handleRefresh} />;
    }

    if (showInitialSyncSkeleton) {
        return (
            <div className="flex-1 flex flex-col min-h-0">
                <PersonalInboxToolbar
                    filters={filters}
                    onChange={handleFilterChange}
                    resultCount={0}
                    totalCount={0}
                    onSync={() => handleSync()}
                    isSyncing={true}
                    canSync={anyConnected}
                    selectedCount={0}
                    onBulkDelete={() => {}}
                    onClearSelection={() => {}}
                    isDeleting={false}
                />
                <div
                    className="flex-1 flex flex-col items-center justify-center gap-4 bg-stone-50"
                    role="status"
                    aria-live="polite"
                    data-testid="personal-inbox-initial-sync"
                >
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" aria-hidden="true" />
                    <div className="text-center">
                        <p className="text-sm font-medium text-stone-700">
                            Syncing your inbox…
                        </p>
                        <p className="text-xs text-stone-500 mt-1">
                            Pulling your latest mail from {gmailConnected && outlookConnected
                                ? 'Gmail and Outlook'
                                : gmailConnected ? 'Gmail' : 'Outlook'}.
                        </p>
                    </div>
                    <InboxSkeleton rows={5} />
                </div>
            </div>
        );
    }

    if (showSyncPrompt || showConnectPrompt) {
        return (
            <SyncInboxPrompt
                gmailConnected={gmailConnected}
                outlookConnected={outlookConnected}
                isSyncing={isSyncing}
                onSync={() => handleSync()}
                onGoToSettings={() => navigate('/settings/integrations')}
            />
        );
    }

    if (showSyncError) {
        return (
            <div className="flex-1 flex flex-col min-h-0">
                <PersonalInboxToolbar
                    filters={filters}
                    onChange={handleFilterChange}
                    resultCount={0}
                    totalCount={0}
                    onSync={() => handleSync()}
                    isSyncing={isSyncing}
                    canSync={anyConnected}
                    selectedCount={0}
                    onBulkDelete={() => {}}
                    onClearSelection={() => {}}
                    isDeleting={false}
                />
                <div
                    className="flex-1 flex flex-col items-center justify-center gap-4 bg-stone-50 p-8"
                    role="alert"
                    aria-live="assertive"
                    data-testid="personal-inbox-sync-error"
                >
                    <p className="text-sm font-medium text-stone-700">
                        Sync failed — we couldn't pull your mail.
                    </p>
                    <p className="text-xs text-stone-500 text-center max-w-sm">
                        Your provider may be temporarily unavailable or your
                        connection may need refreshing.
                    </p>
                    <button
                        onClick={() => handleSync()}
                        disabled={isSyncing}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="personal-inbox-sync-retry"
                    >
                        {isSyncing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Syncing…
                            </>
                        ) : (
                            'Try again'
                        )}
                    </button>
                </div>
            </div>
        );
    }

    return (
        /* `min-h-0` is load-bearing on every flex ancestor between the page
           shell and the scroll container. A flex item defaults to
           `min-height: auto`, which lets it grow past its parent's height
           instead of shrinking — so this wrapper expanded to fit all loaded
           rows, the `overflow-y-auto` child inside PersonalInboxTable was
           handed that full height and never scrolled, and the
           IntersectionObserver sentinel never entered a viewport. Pagination
           looked broken. The previous fix applied min-h-0 to the innermost
           container only, which cannot work while its ancestors still expand. */
        <div className="flex-1 flex flex-col min-h-0">
            <PersonalInboxToolbar
                filters={filters}
                onChange={handleFilterChange}
                resultCount={allRows.length}
                totalCount={data?.total ?? 0}
                onSync={() => handleSync()}
                isSyncing={isSyncing}
                canSync={anyConnected}
                onCompose={() => navigate('/inbox/compose')}
                /* The chips double as a health signal: green when the provider's
                   connection is live, amber when it needs reconnecting, and the
                   chip is absent when that provider was never connected — a
                   filter for a mailbox you do not have can only return nothing. */
                sources={{
                    gmail: gmailConnected
                        ? (gmailStatus?.needs_reconnect ? 'degraded' : 'healthy')
                        : null,
                    outlook: outlookConnected
                        ? (outlookStatus?.needs_reconnect ? 'degraded' : 'healthy')
                        : null,
                }}
                onSettings={() => navigate('/settings/integrations')}
                /* Selection now lives in the BulkBar inside the table, which
                   owns the count, the actions and the "select all N matching"
                   escalation. Two controls for one selection is how they drift
                   apart. */
                selectedCount={0}
                isDeleting={deleteMutation.isPending}
            />

            {/* Rail and list share a row BENEATH the control strip, which spans
                the full width above them — §10's shell, and what the prototype
                draws. The rail is IN FLOW: side navs push content at desktop
                widths, they never overlay. `min-h-0` on both columns for the
                same reason it is on the wrapper. */}
            <div className="flex-1 flex min-h-0">
            <ConnectedFolderRail
                activeView={folder}
                onSelectView={handleSelectFolder}
                activeTag={filters.tag || null}
                onSelectTag={handleSelectTag}
                canTag
                canManageTags={Boolean(user?.is_admin)}
                onManageTags={() => navigate('/admin/tags')}
            />

            {/* The list gets its own COLUMN so the Trash bar can span it.
                As a sibling of the rail and the table it was a third column
                that squeezed the list; `min-h-0` here for the same reason the
                other two columns carry it — without it the table loses its
                scroll containment. */}
            <div className="relative flex-1 flex flex-col min-h-0">
                {folder === 'trash' && (
                    /* The third state in #692's model. Delete MOVES to Trash;
                       Empty Trash is the only control that makes it permanent,
                       so it exists nowhere else and confirms before it runs —
                       and it is withheld when there is nothing to empty, while
                       the retention notice stays. */
                    <TrashNoticeBar
                        onEmpty={() => setConfirmEmptyTrash(true)}
                        isEmptying={emptyTrash.isPending}
                        canEmpty={allRows.length > 0}
                    />
                )}

                {/* Offered only to a connection that predates the job model —
                    see `SyncBackfillPrompt`. This surface is where the
                    connection state lives, which is why it renders here rather
                    than with the rest of the first-sync chrome on the page. */}
                <SyncBackfillPrompt
                    providers={[
                        { id: 'gmail', connected: gmailConnected },
                        { id: 'outlook', connected: outlookConnected },
                    ]}
                />
                {bulkTag.panel}
                <PersonalInboxTable
                    entries={allRows}
                    selection={selection}
                    onRowClick={handleRowClick}
                    onAction={handleAction}
                    folderLabel={folder}
                    isLoading={isLoading && allRows.length === 0}
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={loadMore}
                />
            </div>
            </div>

            <AlertDialog
                open={confirmEmptyTrash}
                onOpenChange={(open) => !open && setConfirmEmptyTrash(false)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Empty Trash?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Everything in Trash will be permanently deleted from
                            NurtureCRM. This cannot be undone. Your mailbox at Gmail or
                            Outlook is not touched.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runEmptyTrash}
                            data-testid="empty-trash-confirm"
                        >
                            Delete everything
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={Boolean(pendingDelete)}
                onOpenChange={(open) => !open && setPendingDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{deleteLabel}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteMixed
                                ? `The draft${deleteDraftCount === 1 ? '' : 's'} cannot be
                                   recovered — a draft has no Trash to go to. The
                                   ${deleteMessageCount === 1 ? 'conversation' : 'conversations'}
                                   ${deletePermanent
                                       ? 'will be removed from NurtureCRM for good'
                                       : 'will move to Trash, where you can restore them later'}.
                                   Your mailbox at Gmail or Outlook is not touched.`
                                : deleteDraftsOnly
                                ? `This cannot be undone — a draft has no Trash to go to.
                                   ${deleteCount === 1 ? 'It was' : 'They were'} never sent, so
                                   nothing leaves your mailbox at Gmail or Outlook.`
                                : deletePermanent
                                ? `This cannot be undone. ${deleteCount === 1 ? 'It' : 'They'} will be
                                   removed from NurtureCRM for good. Your mailbox at Gmail or
                                   Outlook is not touched.`
                                : `${deleteCount === 1 ? 'It' : 'They'} will move to Trash, where you
                                   can restore ${deleteCount === 1 ? 'it' : 'them'} later. Your mailbox
                                   at Gmail or Outlook is not touched.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            data-testid="personal-inbox-delete-confirm"
                        >
                            {deleteDraftsOnly
                                ? 'Delete draft'
                                : deleteMixed
                                  ? 'Delete'
                                  : deletePermanent
                                    ? 'Delete forever'
                                    : 'Move to Trash'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
